'use client'

import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useSession } from 'session/src/hooks/useSession'
import { type KeyedMutator, default as useSwrImmutable } from 'swr'
import { TOTAL_ITEMS_KEY } from '../../constants/constants'
import { useCartState } from '../../context/CartProvider'
import {
	FindTotalItemsByUserDocument,
	type FindTotalItemsByUserQuery,
} from '../../services/queries/find-total-items-by-user.generated'
import { cartTotalItemsCache } from '../../utils/cache/cartTotalItemsCache'
import { useUpdateTotalItems } from '../mutations/useUpdateTotalItems'
import { useCartFetcher } from '../useCartFetcher'

interface UseBagTotalItemsType {
	totalItems: number | undefined
	mutate: KeyedMutator<FindTotalItemsByUserQuery>
	error: Error[] | null
}

interface CacheFetcherProps {
	token: string
	countryISO: string
}

interface UseBagTotalItemsProps {
	errorCallback?: () => void
}

const cacheFetcher = async (args: CacheFetcherProps) => {
	const cacheInstance = cartTotalItemsCache({
		token: args.token,
		countryISO: args.countryISO,
	})
	const totalItemsCache: string | undefined =
		await cacheInstance.getKeyFromCache<string>(TOTAL_ITEMS_KEY)
	if (totalItemsCache !== undefined) {
		return totalItemsCache
	}
	throw new Error('no totalItem in cache')
}

export const useBagTotalItems = ({
	errorCallback,
}: UseBagTotalItemsProps = {}): UseBagTotalItemsType => {
	const { showShoppingCart } = useCartState()
	const { fetcher } = useCartFetcher<
		FindTotalItemsByUserQuery,
		Record<string, never>
	>()
	const { updateTotalItems } = useUpdateTotalItems()
	const { token } = useSession()

	const {
		country: { countryISO },
	} = useMasterData()

	// query to handle cache storage total items
	useSwrImmutable(
		token && countryISO ? { token, countryISO } : null,
		cacheFetcher,
		{
			onSuccess: (cacheData) => void updateTotalItems(Number(cacheData)),

			onError: () => {
				if (
					!showShoppingCart &&
					data?.findTotalItemsByUser?.totalItems === undefined
				) {
					void mutate()
				}
			},
		}
	)

	const { data, error, mutate } = useSwrImmutable<FindTotalItemsByUserQuery>(
		token ? [FindTotalItemsByUserDocument] : null,
		fetcher,
		{
			revalidateOnMount: false,
			onSuccess: (newData) => {
				if (newData?.findTotalItemsByUser?.totalItems !== undefined) {
					void updateTotalItems(newData.findTotalItemsByUser.totalItems)
				}
			},
			onError: () => {
				if (errorCallback) {
					errorCallback()
				}
			},
		}
	)

	return {
		totalItems: data?.findTotalItemsByUser?.totalItems,
		error,
		mutate,
	}
}
